import { Component, DestroyRef, OnInit } from '@angular/core';
import {
  API,
  ClientCullDTO,
  ClientDTO,
  ContentCullDTO,
  DealDTO,
  PlaybackScheduleFullDTO,
  PlaybackScheduleSlotFullDTO,
} from '../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { PlaybackScheduleFullDTOAndTags } from '../../../models/taggable.model';
import { SbPricingModalComponent } from '../../../components/sb-pricing-modal/sb-pricing-modal.component';
import { SearchSelectResult } from '../../../services/gui/modal/modal.model';
import { PermissionService } from '../../../services/permission.service';
import { SlotOrderComponent } from '../../../components/slot-order-component/slot-order.component';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-schedule-details',
  templateUrl: './schedule-details.component.html',
  styleUrls: ['./schedule-details.component.scss'],
  standalone: false,
})
export class ScheduleDetailsComponent implements OnInit {
  schedule: PlaybackScheduleFullDTOAndTags;
  editMode: boolean;
  scheduleId: number;
  client: ClientDTO;

  maxPercentage: number = 100;

  hasCircularDependencyInFallback: boolean = false;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    public readonly modalService: ModalService,
    private readonly api: API,
    public readonly util: UtilService,
    private readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.activatedRoute.params.subscribe({
      next: params => {
        this.scheduleId = params.scheduleID;
        this.refreshFromServer().then();
      },
      error: () => {},
    });
  }

  refreshFromServer() {
    return new Promise(resolve => {
      this.api
        .ss_nm_PlaybackSchedule_retrieveById(this.scheduleId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((e: PlaybackScheduleFullDTO) => {
          this.schedule = e;
          this.refreshMaxPercentage();
          resolve(true);
        });
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.schedule.slots, event.previousIndex, event.currentIndex);
    this.refreshOrderNum();
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.adjustFallbackAfterMove(event);
  }

  adjustFallbackAfterMove(event: CdkDragDrop<string[]>) {
    const leftShift: boolean = event.previousIndex > event.currentIndex;
    for (const slot of this.schedule.slots) {
      if (!slot.fallbackOrderNum || slot.fallbackOrderNum?.length <= 0) {
        continue;
      }
      for (const [index, fallback] of slot.fallbackOrderNum.entries()) {
        if (leftShift) {
          // current index smaller than before
          if (fallback < event.currentIndex || event.previousIndex < fallback) {
            continue;
          }
          if (event.currentIndex <= fallback && fallback < event.previousIndex) {
            slot.fallbackOrderNum[index] = fallback + 1;
          }
          if (fallback === event.previousIndex) {
            slot.fallbackOrderNum[index] = event.currentIndex;
          }
        } else {
          // current index larger than before
          if (fallback > event.currentIndex || event.previousIndex > fallback) {
            continue;
          }
          if (event.previousIndex < fallback && fallback <= event.currentIndex) {
            slot.fallbackOrderNum[index] = fallback - 1;
          }
          if (fallback === event.previousIndex) {
            slot.fallbackOrderNum[index] = event.currentIndex;
          }
        }
      }
    }
  }

  addEntry() {
    const newSlot = new PlaybackScheduleSlotFullDTO();
    (newSlot as unknown as any).orderNum = this.schedule.slots.length;
    this.schedule.slots.push(newSlot);
  }

  deleteEntry(e: any) {
    const index = this.schedule.slots.indexOf(e, 0);
    if (index > -1) {
      this.schedule.slots.splice(index, 1);
    }
    this.schedule.slots.forEach(value => {
      if (value.fallbackOrderNum.includes(e.orderNum)) {
        value.fallbackOrderNum.splice(value.fallbackOrderNum.indexOf(e.orderNum, 0), 1);
      }
    });
    this.refreshOrderNum();
  }

  private refreshOrderNum(): void {
    this.schedule.slots.forEach((slot, idx) => {
      (slot as unknown as any).orderNum = idx;
    });
  }

  refreshMaxPercentage() {
    if (this.schedule?.slots) {
      let sum = 0;
      for (const slot of this.schedule.slots) {
        if (slot.percentage || slot.percentage === 0) {
          sum += slot.percentage;
        }
      }

      this.maxPercentage = 100 - sum;
    }

    this.reapplyFormDebounced();
  }

  adjustMaxPercentage(slot: PlaybackScheduleSlotFullDTO, $event: number) {
    if (typeof $event !== 'number') {
      slot.percentage = undefined;
      return;
    }
    if (this.schedule?.slots) {
      let maxPercentage = 100;
      const index = this.schedule.slots.indexOf(slot, 0);

      for (const fSlot of this.schedule.slots) {
        const iSlot = this.schedule.slots.indexOf(fSlot, 0);
        if (index !== iSlot && (fSlot.percentage || fSlot.percentage === 0)) {
          maxPercentage -= fSlot.percentage;
        }
      }
      if ($event < 0) {
        slot.percentage = undefined;
        slot.percentage = 0;
      } else if ($event > maxPercentage) {
        slot.percentage = undefined;
        slot.percentage = maxPercentage;
      } else {
        slot.percentage = undefined;
        slot.percentage = $event;
      }

      this.refreshMaxPercentage();
    }
  }

  reapplyFormDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.reapplyForm();
    }, 500);
  }

  reapplyForm() {
    if (this.schedule?.slots) {
      this.schedule.slots = this.schedule.slots.splice(0);
    }
  }

  showClients(id: number) {
    if (id) {
      this.modalService
        .modal(
          {
            title: this.translate.instant('lang.globals.preview'),
            modalOptions: { size: 'sm' },
          },
          {
            templateId: 'sbPreviewListModal',
          },
          id
        )
        .then(() => {})
        .catch(() => {});
    }
  }

  setCull(e: PlaybackScheduleSlotFullDTO, type: string) {
    const index = this.schedule.slots.indexOf(e, 0);
    if (type === 'CLIENT') {
      const clientCull: ClientCullDTO = e.clientCull;
      this.modalService
        .searchSelect({
          attributeType: 'CLIENTCULL',
          datatype: null,
          initial: clientCull ? [clientCull] : [],
          singlemode: true,
          allowEmpty: false,
          title: this.translate.instant('lang.globals.selectValueForNamedFilter', {
            name: this.translate.instant('lang.scheduleDetails.clientCull'),
          }),
        })
        .then((res: SearchSelectResult<ClientCullDTO>) => {
          if (!res.cancel) {
            if (res.values) {
              this.schedule.slots[index].clientCull = res.values[0];
            } else {
              this.schedule.slots[index].clientCull = null;
            }
          }
        })
        .catch(() => {});
    }
    if (type === 'CONTENT') {
      const contentCull: ContentCullDTO = e.contentCull;
      this.modalService
        .searchSelect({
          attributeType: 'CONTENTCULL',
          datatype: null,
          initial: contentCull ? [contentCull] : [],
          singlemode: true,
          allowEmpty: false,
          title: this.translate.instant('lang.globals.selectValueForNamedFilter', {
            name: this.translate.instant('lang.scheduleDetails.contentCull'),
          }),
        })
        .then((res: SearchSelectResult<ContentCullDTO>) => {
          if (!res.cancel) {
            if (res.values) {
              this.schedule.slots[index].contentCull = res.values[0];
            } else {
              this.schedule.slots[index].contentCull = null;
            }
          }
        })
        .catch(() => {});
    }
  }

  updateSchedule() {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.confirmation'),
        message: this.translate.instant('lang.globals.confirmationWouldYouLikeToEdit'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ss_nm_PlaybackSchedule_updateById(this.schedule)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (data: PlaybackScheduleFullDTO) => {
                this.schedule = data;
                this.editMode = !this.editMode;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  editDeals(slot: PlaybackScheduleSlotFullDTO) {
    const deals = slot.deals;
    const index = this.schedule.slots.indexOf(slot, 0);
    this.modalService
      .searchSelect({
        attributeType: 'DEAL',
        datatype: null,
        initial: deals,
        allowEmpty: false,
        title: this.translate.instant('lang.globals.selectValueForNamedFilter', { name: this.translate.instant('lang.globals.deals') }),
      })
      .then((e: SearchSelectResult<DealDTO>) => {
        if (!e.cancel) {
          if (e.values) {
            this.schedule.slots[index].deals = e.values;
          } else {
            this.schedule.slots[index].deals = [];
          }
        }
      })
      .catch(() => {});
  }

  enableEditMode() {
    this.editMode = !this.editMode;
  }

  cancelEditMode() {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.confirmation'),
        message: this.translate.instant('lang.globals.discardChangesQuestion'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.refreshFromServer().then(() => {
            this.editMode = !this.editMode;
          });
        }
      })
      .catch(() => {});
  }

  showPricing(playbackSlot: PlaybackScheduleSlotFullDTO, componentDisabled: boolean) {
    const initialSlot = { ...playbackSlot };
    const index = this.schedule.slots.indexOf(playbackSlot, 0);
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SbPricingModalComponent, data: { slot: initialSlot, disabled: componentDisabled } },
        title: playbackSlot.orderNum + 1 ? 'Slot #' + (playbackSlot.orderNum + 1) : 'Slot Pricing',
        modalOptions: { size: 'lg' },
      })
      .then(result => {
        if (result) {
          this.schedule.slots[index] = result as PlaybackScheduleSlotFullDTO;
        }
      })
      .catch(() => {});
  }

  editFallback(slot: PlaybackScheduleSlotFullDTO) {
    // create copy of slots and remove those whose fallback is selected slot
    const slotsCopy: PlaybackScheduleSlotFullDTO[] = [...this.schedule.slots].filter(
      filterSlot => !filterSlot.fallbackOrderNum.includes(slot.orderNum)
    );
    // remove selected slot
    slotsCopy.splice(slotsCopy.indexOf(slot, 0), 1);
    const fallbackCopy: PlaybackScheduleSlotFullDTO[] = [];
    // remove already selected fallbackslots from copy and add to fallback array
    for (const orderNum of slot.fallbackOrderNum) {
      const item: PlaybackScheduleSlotFullDTO = slotsCopy.find(x => x.orderNum === orderNum);
      slotsCopy.splice(slotsCopy.indexOf(item, 0), 1);
      fallbackCopy.push(item);
    }

    //show selection dialog
    this.modalService
      .showNavigationModal({
        entryComponent: { value: SlotOrderComponent, data: { slots: slotsCopy, fallback: fallbackCopy } },
        title: this.translate.instant('lang.globals.fallback') + ' Slot #' + (slot.orderNum + 1),
        modalOptions: { size: 'lg' },
      })
      .then((result: PlaybackScheduleSlotFullDTO[]) => {
        if (result && result.length >= 0) {
          slot.fallbackOrderNum = result.map(fallbackSlot => fallbackSlot.orderNum);
        }
      })
      .catch(() => {})
      .finally(() => {
        this.checkCircularDependency();
      });
  }

  getOrderNums(slot: PlaybackScheduleSlotFullDTO): number[] {
    return slot.fallbackOrderNum.map(x => x + 1);
  }

  checkCircularDependency() {
    // No cycles default
    let result = false;
    const visited = new Set<PlaybackScheduleSlotFullDTO>();
    for (const obj of this.schedule.slots) {
      if (!visited.has(obj) && this.hasCycle(obj, visited)) {
        result = true; // At least one cycle found
      }
    }
    this.hasCircularDependencyInFallback = result;
  }

  private hasCycle(
    obj: PlaybackScheduleSlotFullDTO,
    visited = new Set<PlaybackScheduleSlotFullDTO>(),
    stack = new Set<PlaybackScheduleSlotFullDTO>()
  ): boolean {
    if (stack.has(obj)) {
      // Cycle found
      return true;
    }

    if (visited.has(obj)) {
      return false; // Already checked and no cycle
    }

    visited.add(obj);
    stack.add(obj);

    const fallbackArray = this.schedule.slots.filter(x => obj.fallbackOrderNum.includes(x.orderNum));

    for (const fallback of fallbackArray) {
      if (this.hasCycle(fallback, visited, stack)) {
        return true;
      }
    }

    stack.delete(obj);
    return false;
  }
}
